var _excluded = ["title", "description", "header", "children", "isDisabled", "isInactive", "status", "onClick", "defaultIsExpanded", "isExpanded", "onExpandChange"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import { useCallback, useEffect, useRef, useState } from 'react';
import { Text, Typography } from '../../components/dataDisplay';
import { Divider } from '../../components/dataDisplay/Divider';
import { _NotSupportedBadge, Box } from '../../components/internal';
import { Icon } from '../../foundations/Icon';
import { StatusIconSolid } from '../../foundations/StatusIconSolid';
import { Collapse } from '../../foundations/transitions';
import { forwardRef, mergeRefs } from '../../utils';
import { useMultiStyleConfig } from '../../utils/use-style-config';
import { Card } from '../Card';
import { Group } from '../Group';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export var CollapsibleCard = forwardRef((_ref, forwardRef) => {
  var {
    title,
    description,
    header,
    children,
    isDisabled = false,
    isInactive = false,
    status,
    onClick,
    defaultIsExpanded = false,
    isExpanded,
    onExpandChange
  } = _ref,
      props = _objectWithoutProperties(_ref, _excluded);

  var styles = useMultiStyleConfig('CollapsibleCard', {});
  var collapsibleCardHeaderRef = useRef(null);
  var [localIsExpanded, setLocalIsExpanded] = useState(Boolean(defaultIsExpanded || isExpanded));
  var isControlled = isExpanded !== undefined;
  var cardRef = useRef(null);
  var ref = mergeRefs([forwardRef, cardRef]);
  var handleOnClick = useCallback(e => {
    var _collapsibleCardHeade;

    var isCurrentCardTarget = e.target === cardRef.current;
    var isCurrentCardHeaderTarget = e.target === collapsibleCardHeaderRef.current;

    if (isCurrentCardHeaderTarget || (_collapsibleCardHeade = collapsibleCardHeaderRef.current) !== null && _collapsibleCardHeade !== void 0 && _collapsibleCardHeade.contains(e.target) || // This is to handle the case when the user use the keyboard to navigate and press enter or space
    isCurrentCardTarget) {
      var updatedExpanded = !localIsExpanded;
      onExpandChange === null || onExpandChange === void 0 ? void 0 : onExpandChange(updatedExpanded);
      onClick === null || onClick === void 0 ? void 0 : onClick(e);
      if (isControlled) return;
      setLocalIsExpanded(updatedExpanded);
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [localIsExpanded]);
  useEffect(() => {
    if (isControlled) {
      setLocalIsExpanded(isExpanded);
    }
  }, [isControlled, isExpanded, localIsExpanded]); // TODO: Add type guard and move not supported badge into the story https://meliorisk.atlassian.net/browse/ME-44253

  var isNotSupported = isInactive && (defaultIsExpanded || isExpanded) || isDisabled && (defaultIsExpanded || isExpanded);

  if (isNotSupported) {
    return /*#__PURE__*/_jsx(_NotSupportedBadge, {
      details: {
        componentName: 'CollapsibleCard',
        props: {
          defaultIsExpanded,
          isExpanded,
          isDisabled,
          isInactive
        }
      }
    });
  }

  return /*#__PURE__*/_jsx(Card, _objectSpread(_objectSpread({
    "data-component": "CollapsibleCard",
    ref: ref,
    paddingX: "none",
    paddingY: "none",
    onClick: handleOnClick,
    isDisabled: isDisabled,
    isInactive: isInactive
    /* Is used for accessibility reasons to help screen readers detect the expand/collapse state */
    ,
    "aria-expanded": localIsExpanded,
    "data-testid": "collapsible-card"
  }, props), {}, {
    children: /*#__PURE__*/_jsxs(Group, {
      variant: "vertical",
      spacing: "none",
      width: "full",
      children: [/*#__PURE__*/_jsx(Box, {
        ref: collapsibleCardHeaderRef,
        __css: styles['header'],
        "data-inactive": isInactive || null,
        "aria-disabled": isDisabled || undefined,
        "data-testid": "collapsible-card-header",
        "data-state": localIsExpanded ? 'expanded' : 'collapsed',
        children: header ? /*#__PURE__*/_jsx(Box, {
          __css: styles['customHeader'],
          "data-inactive": isInactive || null,
          "aria-disabled": isDisabled || undefined,
          children: header
        }) : /*#__PURE__*/_jsxs(Group, {
          variant: "vertical",
          spacing: "xxs",
          width: "full",
          children: [/*#__PURE__*/_jsxs(Group, {
            spacing: "xxs",
            alignItems: "center",
            justifyContent: "space-between",
            children: [/*#__PURE__*/_jsxs(Group, {
              spacing: "xs",
              alignItems: "center",
              children: [/*#__PURE__*/_jsx(Text, {
                as: "h2",
                textStyle: "heading3Semi",
                color: "inherit",
                children: title
              }), status && /*#__PURE__*/_jsx(StatusIconSolid, {
                variant: status,
                size: "medium",
                isReadOnly: isInactive,
                isDisabled: isDisabled
              })]
            }), /*#__PURE__*/_jsx(Icon, {
              type: localIsExpanded ? 'chevron-up' : 'chevron-down',
              size: "large",
              color: "inherit"
              /* Is used for accessibility reasons to prevent an element from being read by a screen reader. */
              ,
              "aria-hidden": "true"
            })]
          }), description && /*#__PURE__*/_jsx(Typography.Description, {
            label: description,
            isReadOnly: isInactive,
            isDisabled: isDisabled
          })]
        })
      }), /*#__PURE__*/_jsx(Collapse, {
        "data-testid": "collapsible-card-collapse",
        in: localIsExpanded,
        children: /*#__PURE__*/_jsxs(Box, {
          __css: styles['content'],
          children: [/*#__PURE__*/_jsx(Divider, {}), children]
        })
      })]
    })
  }));
});
CollapsibleCard.displayName = 'CollapsibleCard';
//# sourceMappingURL=CollapsibleCard.js.map